import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiFetch from "../../utlis/apiFetch";
import { forceLogout } from "../../utlis/forceLogout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getAuthToken = () => localStorage.getItem("token");

const handleAuthError = (error, rejectWithValue) => {
  if (error.message === "Your session has expired") forceLogout();
  return rejectWithValue(error.message);
};

export const fetchAgents = createAsyncThunk(
  "agents/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token)
        throw new Error("No authentication token found. Please log in.");

      const response = await apiFetch(`${API_BASE_URL}/api/user/getAllAgents`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response._data ?? await response.json();
      if (!response.ok) throw new Error(data.message || "Something went wrong");

      return data.body;
    } catch (error) {
      return handleAuthError(error, rejectWithValue);
    }
  }
);

export const createAgent = createAsyncThunk(
  "agents/create",
  async (formData, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token)
        throw new Error("No authentication token found. Please log in.");

      const response = await apiFetch(`${API_BASE_URL}/api/user/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = response._data ?? await response.json();
      if (!response.ok) throw new Error(data.message || "Something went wrong");

      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || "An error occurred while creating an agent"
      );
    }
  }
);

const agentSlice = createSlice({
  name: "agents",
  initialState: {
    agents: [],
    isLoading: false,
    isCreating: false,   
    error: null,
    success: false,
  },
  reducers: {
    clearAgents: (state) => {
      state.agents = [];
      state.isLoading = false;
      state.isCreating = false;
      state.error = null;
      state.success = false;
    },
    resetSuccess: (state) => {
      state.success = false;
    },
    // Add this reducer to update agent in the agents list
    updateAgentInList: (state, action) => {
      const updatedAgent = action.payload;
      const index = state.agents.findIndex(agent => agent._id === updatedAgent._id);
      if (index !== -1) {
        state.agents[index] = { ...state.agents[index], ...updatedAgent };
      }
    },
    // Add this reducer to remove agent from the agents list (for deactivation)
    removeAgentFromList: (state, action) => {
      const agentId = action.payload;
      state.agents = state.agents.filter(agent => agent._id !== agentId);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.agents = action.payload;
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createAgent.pending, (state) => {
        state.isCreating = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createAgent.fulfilled, (state, action) => {
        state.isCreating = false;
        state.success = true;
        if (action.payload && action.payload.body && action.payload.body.newUser) {
          // Extract newUser from the response and ensure isActive is included
          const newAgent = {
            ...action.payload.body.newUser,
            isActive: action.payload.body.newUser.isActive !== undefined ? action.payload.body.newUser.isActive : true,
          };
          state.agents.push(newAgent);
        } else {
          console.error("Invalid API response:", action.payload);
        }
      })
      .addCase(createAgent.rejected, (state, action) => {
        state.isCreating = false;
        state.success = false;
        state.error = action.payload;
      });
  },
});

export const { clearAgents, resetSuccess, updateAgentInList, removeAgentFromList } = agentSlice.actions;
export default agentSlice.reducer;